/**
 * User-defined document workspaces (card 4.0, option A; card 5.1) — the PURE
 * half. Records live in the one personal bucket; a ROOT folder/document may
 * carry `scopeId` in its data (absent = Personal) and every descendant belongs
 * to its root's workspace. Nested rows never carry one (and a stray one on a
 * nested row is ignored — the root decides).
 *
 * Descriptor rule, deliberately three-valued so no legacy caller changes
 * behaviour:
 *   { type: 'personal' }                  — the whole bucket (git sync, MCP,
 *                                            locateDocument: every workspace)
 *   { type: 'personal', scopeId: null }   — Personal only
 *   { type: 'personal', scopeId: 'abc' }  — workspace `abc` only
 * Device and team scopes have no sub-workspaces; a scopeId on them is ignored.
 *
 * Nothing here touches records, the DOM or a store — documentsStore.js and
 * documentsScopes.js do the I/O.
 */

/** The envelope keys Rust's `record_json` spreads over a row's `data`. */
const ENVELOPE_KEYS = ['id', 'record_type', 'parent_id', 'sort_key'];

/** A listed row's own `data` (everything but the envelope) — safe to write back. */
export function dataOf(row) {
  const data = {};
  for (const [key, value] of Object.entries(row || {})) {
    if (!ENVELOPE_KEYS.includes(key)) data[key] = value;
  }
  return data;
}

/**
 * The workspace filter a descriptor asks for: a string id, `null` for Personal
 * only, or `undefined` for "no filter" (whole bucket, the legacy meaning).
 */
export function scopeFilterOf(scope) {
  const personal = !scope || !scope.type || scope.type === 'personal';
  if (!personal || scope?.scopeId === undefined) return undefined;
  return scope.scopeId || null;
}

/** The bucket-level descriptor for `scope` — the same records, no workspace filter. */
export function bucketScopeOf(scope) {
  if (scopeFilterOf(scope) === undefined) return scope;
  const { scopeId, ...rest } = scope;
  return rest;
}

/**
 * The `scopeId` a NEW node is stamped with: only a root, only in a personal
 * workspace. `target` is the scope the write actually lands in (after the
 * device fallback), so a root that spilled onto this device carries none.
 */
export function rootScopeStamp(target, parentId) {
  if (parentId != null) return {};
  const id = scopeFilterOf(target);
  return id ? { scopeId: id } : {};
}

/**
 * `data` without its workspace. A row that carried a `scopeId` gets an explicit
 * `null`, never a missing key: the sync push sends `data` as `set` with an empty
 * `unset`, and the server keeps any key an update leaves out — deleting the
 * field locally would leave the old workspace on the server (and on every other
 * device). A row that never had one stays without the key.
 */
export function clearedScope(data) {
  const out = { ...data };
  if (Object.prototype.hasOwnProperty.call(out, 'scopeId')) out.scopeId = null;
  return out;
}

/**
 * `data` for a node being moved to `newParentId` under `scope`: a nested node
 * loses any scopeId, a root takes the descriptor's workspace — or keeps its own
 * when the descriptor is bucket-level (legacy callers: a plain reorder).
 * "Loses" = `clearedScope` (explicit null, see there).
 */
export function movedData(data, newParentId, scope) {
  if (newParentId != null) return clearedScope(data);
  const id = scopeFilterOf(scope);
  if (id === undefined) return { ...data };
  return id ? { ...data, scopeId: id } : clearedScope(data);
}

/**
 * id -> resolved workspace (string or null) for every node, walking each up to
 * its root. `ownScopeId` is the raw value on the row; `known`, when given, is
 * the set of live docscope ids — a root naming a workspace that no longer
 * exists (deleted on another device before the moves synced) falls back to
 * Personal rather than vanishing from every view.
 *
 * Cycle-safe and orphan-safe: a node whose parent is missing, or whose chain
 * loops, is treated as its own root.
 */
export function resolveScopeIds(nodes, known = null) {
  const byId = new Map((nodes || []).map((n) => [String(n.id), n]));
  const memo = new Map();
  const norm = (id) => (id && (!known || known.has(id)) ? id : null);
  const resolve = (node) => {
    const chain = [];
    const seen = new Set();
    let cur = node;
    let found;
    for (;;) {
      const key = String(cur.id);
      if (memo.has(key)) {
        found = memo.get(key);
        break;
      }
      seen.add(key);
      chain.push(key);
      const parent = cur.parentId == null ? null : byId.get(String(cur.parentId));
      if (!parent || seen.has(String(parent.id))) {
        found = norm(cur.ownScopeId);
        break;
      }
      cur = parent;
    }
    for (const key of chain) memo.set(key, found);
    return found;
  };
  for (const node of nodes || []) resolve(node);
  return memo;
}

/**
 * Tags each node with its resolved `scopeId` (only when not Personal, so the
 * node shape of every existing scope-less tree is byte-identical) and keeps
 * the ones in `filter` (`undefined` keeps everything). `ownScopeId` is dropped.
 */
export function applyScopeFilter(nodes, filter, known = null) {
  const resolved = resolveScopeIds(nodes, known);
  const out = [];
  for (const node of nodes) {
    const { ownScopeId, ...rest } = node;
    const scopeId = resolved.get(String(node.id)) ?? null;
    if (filter !== undefined && scopeId !== filter) continue;
    out.push(scopeId ? { ...rest, scopeId } : rest);
  }
  return out;
}
